import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './BenchScales.css';
import { useProducts, type BenchScalesSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const BenchScales: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: models, loading, error } = useProducts<BenchScalesSpecs>('bench-scales', editMode);
  const navigate = useNavigate();

  const optionalAccessories = [
    'Plataformas con bolas transferidoras',
    'Batería recargable',
    'Kits de montaje en pared/poste',
    'Cubiertas protectoras',
    'Indicadores remotos',
    'Software de pesaje'
  ];

  const protectionClasses = [
    {
      title: 'IP65',
      description: 'Protección total contra polvo y resistencia a chorros de agua de baja presión.',
      details: 'Ideal para ambientes industriales generales.',
    },
    {
      title: 'IP69K',
      description: 'Máxima protección contra polvo y agua a alta presión y temperatura.',
      details: 'Diseñado para lavado intensivo en la industria alimentaria.',
    },
  ];

  const applications = [
    {
      title: 'Laboratorios',
      description: 'Pesaje de precisión para análisis químicos, muestras y reactivos.',
    },
    {
      title: 'Control de Calidad',
      description: 'Verificación de peso de productos y componentes en procesos de manufactura.',
    },
    {
      title: 'Industria Alimentaria',
      description: 'Pesaje sanitario de ingredientes y productos con certificación NSF.',
    },
    {
      title: 'Farmacéutica',
      description: 'Pesaje de precisión de principios activos y formulaciones farmacéuticas.',
    },
    {
      title: 'Química',
      description: 'Dosificación y control de materias primas en procesos químicos.',
    },
    {
      title: 'Manufactura',
      description: 'Pesaje de componentes y materiales en líneas de producción continua.',
    },
  ];

  return (
    <div className="bsc-page">
      {error && <div className="bsc-status-bar bsc-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="bsc-hero">
        <div className="container bsc-hero-content">
          <nav className="bsc-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="bsc-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="bsc-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Básculas de Banco</span>
          </nav>
          <h1 className="bsc-hero-title">Básculas de Banco</h1>
          <p className="bsc-hero-subtitle">
            Plataformas de pesaje de precisión para laboratorios, control de calidad
            y aplicaciones industriales con tecnología Quartzell y certificación NSF.
          </p>
          {authType === 'internal' && (
            <button
              className={`bsc-btn-edit-mode${editMode ? ' bsc-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="bsc-hero-image" aria-hidden="true">
          <img src="/images/bench-scales.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="bsc-overview">
        <div className="container">
          <div className="bsc-overview-grid">
            <div className="bsc-overview-text">
              <span className="bsc-label">Descripción general</span>
              <h2>Básculas de Banco Profesionales</h2>
              <p>
                Nuestras básculas de banco ofrecen plataformas de pesaje de precisión para procesos
                de pequeño a mediano tamaño. Ideales para pesar desde materias primas hasta productos
                terminados, disponibles en diversos materiales según sus necesidades específicas.
              </p>
              
            </div>
            <div className="bsc-overview-image">
              <ul className="bsc-features-list" aria-label="Características principales">
                {[
                  'Tecnología Quartzell con cristales de cuarzo para máxima precisión',
                  'Disponibles en acero al carbón y acero inoxidable grado alimentario',
                  'Resistentes al agua y ambientes rigurosos',
                  'Configuraciones de alta precisión hasta 500,000 divisiones',
                  'Certificaciones NSF para uso alimentario',
                  'Protección IP65 e IP69K contra agua y polvo',
                ].map((item, i) => (
                  <li key={i} className="bsc-feature-item">
                    <svg className="bsc-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="bsc-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos Disponibles</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/bench-scales')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : models.map((model, index) => (
                <ProductCard
                  key={index}
                  reviewed={model.reviewed}
                  endOfLife={model.endOfLife}
                  productId={model.productId}
                  name={model.name}
                  badge={model.specs?.model ?? undefined}
                  description={model.specs?.description ?? undefined}
                  image={model.image}
                  onRevisar={editMode && model.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(model.specs?.connectivity?.length ? [{ icon: '🔌', label: 'Conectividad',    items: (model.specs?.connectivity ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.protection?.length   ? [{ icon: '🛡️', label: 'Protección',      items: (model.specs?.protection   ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.features?.length     ? [{ icon: '⚙️', label: 'Características', items: (model.specs?.features     ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.capacities?.length   ? [{ icon: '📦', label: 'Capacidades',     items: (model.specs?.capacities   ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Protection classes ───────────────────────────────── */}
      <section className="bsc-protection">
        <div className="container">
          <span className="bsc-label">Certificaciones</span>
          <h2 className="bsc-section-title">Clases de Protección</h2>
          <p className="bsc-section-intro">
            Seleccione el nivel de protección adecuado para el entorno
            de operación de su báscula de banco.
          </p>
          <div className="bsc-protection-grid">
            {protectionClasses.map((item, index) => (
              <div key={index} className="bsc-protection-card">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <p className="bsc-protection-detail">{item.details}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="bsc-applications">
        <div className="container">
          <span className="bsc-label bsc-label--light">Industrias</span>
          <h2 className="bsc-section-title bsc-section-title--light">Aplicaciones</h2>
          <div className="bsc-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="bsc-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Optional accessories ─────────────────────────────── */}
      <section className="bsc-accessories">
        <div className="container">
          <div className="bsc-accessories-inner">
            <div className="bsc-accessories-header">
              <span className="bsc-label">Personalización</span>
              <h2 className="bsc-section-title">Accesorios Opcionales</h2>
              <p>
                Complemente su báscula de banco con accesorios diseñados
                para ampliar su funcionalidad en su proceso productivo.
              </p>
            </div>
            <ul className="bsc-accessory-list" aria-label="Accesorios opcionales">
              {optionalAccessories.map((accessory, index) => (
                <li key={index} className="bsc-accessory-item">
                  <svg className="bsc-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {accessory}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BenchScales;
